import { fileConvert } from "@dopplexr/editor/lib/HDRencoding.ts";
import type { Extras as CameraExtras } from "@dopplexr/gltf-extensions/lib/Camera.ts";
import { DoppleHDRLoader } from "@dopplexr/gltf-extensions/lib/DoppleHDRLoader.ts";
import { ANIMATION_PLAYBACK_VALUES } from "@dopplexr/gltf-extensions/lib/DPLAnimationLoopExtension.ts";
import { EXTENSION_NAME as loadNodesOnDemandExtension } from "@dopplexr/gltf-extensions/lib/DPLLoadNodesOnDemand.ts";
import { GLTFMaterialEnvironmentExtension } from "@dopplexr/gltf-extensions/lib/DPLMaterialEnvExtension.ts";
import { DPLNodeLocatorExtension } from "@dopplexr/gltf-extensions/lib/DPLNodeLocatorExtension.ts";
import { upscalePendingTextures } from "@dopplexr/gltf-extensions/lib/GLTFTextureProgressiveExtension.ts";
import type { Matrix, Selection } from "@dopplexr/matrix/lib/matrix.ts";
import { mapAsync } from "@dopplexr/utils/lib/array.ts";
import { debounce } from "@dopplexr/utils/lib/debounce.ts";
import { throttle } from "@dopplexr/utils/lib/throttle.ts";
import type { GLTF as GLTFTypes } from "@gltf-transform/core";
import { createLogWriter } from "@roarr/browser-log-writer";
import CameraControls from "camera-controls";
import { err, ok, Result, ResultAsync } from "neverthrow";
import QRCode from "qrcode";
import { type Logger, type LogWriter, Roarr as log, ROARR } from "roarr";
import {
	type AnimationAction,
	AnimationMixer,
	Box3,
	type BufferGeometry,
	Clock,
	Color,
	EquirectangularReflectionMapping,
	FloatType,
	Group,
	Line,
	LoadingManager,
	Material,
	MathUtils,
	Matrix4,
	Mesh,
	Object3D,
	type OrthographicCamera,
	PerspectiveCamera,
	PMREMGenerator,
	Points,
	PropertyBinding,
	Quaternion,
	Raycaster,
	REVISION,
	Scene,
	Sphere,
	Spherical,
	Texture,
	type ToneMapping,
	Vector2,
	Vector3,
	Vector4,
	WebGLRenderer,
	WebGLRenderTarget,
} from "three";
import { RoomEnvironment } from "three/addons/environments/RoomEnvironment.js";
import { DRACOLoader } from "three/addons/loaders/DRACOLoader.js";
import { type GLTF, GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import { KTX2Loader } from "three/addons/loaders/KTX2Loader.js";
import { EffectComposer } from "three/addons/postprocessing/EffectComposer.js";
import { OutputPass } from "three/addons/postprocessing/OutputPass.js";
import { RenderPass } from "three/addons/postprocessing/RenderPass.js";
import { ShaderPass } from "three/addons/postprocessing/ShaderPass.js";
import { TAARenderPass } from "three/addons/postprocessing/TAARenderPass.js";
import { UnrealBloomPass } from "three/addons/postprocessing/UnrealBloomPass.js";
import {
	CSS2DObject as CSSObject,
	CSS2DRenderer as CSSRenderer,
} from "three/addons/renderers/CSS2DRenderer.js";
import { BrightnessContrastShader } from "three/addons/shaders/BrightnessContrastShader.js";
import { ExposureShader } from "three/addons/shaders/ExposureShader.js";
import { reduceVertices } from "three/addons/utils/SceneUtils.js";
import * as WebGLTextureUtils from "three/addons/utils/WebGLTextureUtils.js";
import { acceleratedRaycast, MeshBVH } from "three-mesh-bvh";
import { Damper } from "./ar/Damper.ts";
import {
	fitAndRepositionCameraToProduct,
	updateCameraClippingPlanes,
} from "./camera/Camera.ts";
import { getProductBaseURL, loadFile } from "./connectors/s3.ts";
import { KeyboardControl } from "./controls/keyboardControl.ts";

const TWO_PI = 2 * Math.PI;

const browserLogWriter = createLogWriter();
const transportLayers = new Map<DoppleXR, Set<LogWriter>>();
ROARR.write = (...args) => {
	browserLogWriter(...args);
	for (const [_, logWritersSet] of transportLayers) {
		for (const logWriter of logWritersSet) {
			logWriter(...args);
		}
	}
};

Mesh.prototype.raycast = acceleratedRaycast;

CameraControls.install({
	THREE: {
		Vector2,
		Vector3,
		Vector4,
		Quaternion,
		Matrix4,
		Spherical,
		Box3,
		Sphere,
		Raycaster,
	},
});

const checkParentVisibility = (
	nodeIndex: number,
	nodes: GLTFTypes.INode[],
	nodesVisibility: { [nodeId: string]: { visibility: boolean } },
): boolean => {
	let currentIndex = nodeIndex;

	while (true) {
		const parentIndex = nodes.findIndex((node) => {
			const childrenInExtension = node.extensions?.[
				loadNodesOnDemandExtension
			] as Record<string, unknown> & {
				children: number[];
			};
			return (
				node.children?.includes(currentIndex) ||
				childrenInExtension?.children?.includes(currentIndex)
			);
		});

		if (parentIndex === -1) {
			break;
		}

		const parentNode = nodes[parentIndex];
		const parentNodeId = parentNode.name;

		if (parentNodeId && nodesVisibility[parentNodeId]?.visibility === false) {
			return false;
		}

		currentIndex = parentIndex;
	}

	return true;
};

const loadNode = async (
	nodeIndex: number,
	gltf: GLTF,
	nodes: { [nodeId: string]: { visibility: boolean } },
): Promise<Result<Object3D, Error>> => {
	const parentIndex: number =
		gltf.parser.json.nodes?.findIndex((node: GLTFTypes.INode) => {
			const childrenInExtension = node.extensions?.[
				loadNodesOnDemandExtension
			] as Record<string, unknown> & {
				children: number[];
			};
			return (
				node.children?.includes(nodeIndex) ||
				childrenInExtension?.children?.includes(nodeIndex)
			);
		}) ?? -1;
	if (parentIndex !== -1) {
		const isParentVisible = checkParentVisibility(
			nodeIndex,
			gltf.parser.json.nodes ?? [],
			nodes,
		);

		if (isParentVisible) {
			const meshNodeRes: Result<Object3D, Error> =
				await ResultAsync.fromPromise(
					gltf.parser.getDependency("node", nodeIndex),
					() => new Error("Error loading node"),
				);
			if (meshNodeRes.isErr()) {
				return meshNodeRes;
			}

			const meshNode = meshNodeRes.value;

			if (meshNode instanceof Mesh) {
				meshNode.geometry.boundsTree ??= new MeshBVH(meshNode.geometry);
			}

			const parentNodeRes = await loadNode(parentIndex, gltf, nodes);

			if (parentNodeRes.isOk()) {
				const parentNode = parentNodeRes.value;
				parentNode.add(meshNode);
				return meshNodeRes;
			}
			return parentNodeRes;
		}
	} else {
		const meshNodeRes: Result<Object3D, Error> = await ResultAsync.fromPromise(
			gltf.parser.getDependency("node", nodeIndex),
			() => new Error("Error loading node"),
		);

		if (meshNodeRes.isErr()) {
			return meshNodeRes;
		}

		const meshNode = meshNodeRes.value;

		if (meshNode instanceof Mesh) {
			meshNode.geometry.boundsTree = new MeshBVH(meshNode.geometry);
		}
		gltf.scene.add(meshNode);
		return meshNodeRes;
	}

	return ok(new Object3D());
};

const isRenderable = (object: Object3D): object is Mesh | Points | Line =>
	object instanceof Mesh || object instanceof Points || object instanceof Line;

const collectTextures = (value: unknown, textures: Set<Texture>): void => {
	if (!value) {
		return;
	}

	if (value instanceof Texture) {
		textures.add(value);
		return;
	}

	if (Array.isArray(value)) {
		for (const item of value) {
			collectTextures(item, textures);
		}
	}
};

const disposeMaterial = (material: Material): void => {
	const textures = new Set<Texture>();

	for (const value of Object.values(material) as unknown[]) {
		collectTextures(value, textures);
	}

	for (const texture of textures) {
		texture.dispose();
	}

	material.dispose();
};

const disposeGeometry = (geometry: BufferGeometry): void => {
	geometry.dispose();
};

export function disposeObject(object: Object3D): void {
	object.traverse((child) => {
		if (!isRenderable(child)) {
			return;
		}

		if (child.geometry) {
			disposeGeometry(child.geometry);
		}

		const materials: Material[] = Array.isArray(child.material)
			? child.material.filter((mat): mat is Material => mat instanceof Material)
			: child.material
				? [child.material]
				: [];

		for (const material of materials) {
			disposeMaterial(material);
		}
	});
}

function getValueOrDefault(
	value: number | undefined | null,
	defaultValue: number,
): number {
	return value === 0 || value ? value : defaultValue;
}

function createGrabCursor(
	element: HTMLElement,
	cameraControls: CameraControls,
) {
	let isDragging = false;
	let isPointerOver = false;

	element.style.cursor = "default";

	const setCursor = (target: HTMLElement | HTMLBodyElement, value: string) => {
		if (target.style.cursor !== value) {
			target.style.cursor = value;
		}
	};

	const handlePointerEnter = (_e: PointerEvent) => {
		isPointerOver = true;
		if (!isDragging) {
			setCursor(element, "grab");
		}
	};

	const handlePointerLeave = (_e: PointerEvent) => {
		isPointerOver = false;
		if (!isDragging) {
			setCursor(element, "default");
		}
	};

	const handleControlStart = () => {
		isDragging = true;
		setCursor(element, "grabbing");
		setCursor(document.body, "grabbing");
		setChildCursors("inherit");
	};

	const handleControlEnd = () => {
		isDragging = false;
		setCursor(element, isPointerOver ? "grab" : "default");
		setCursor(document.body, "");
		setChildCursors("");
	};

	const setChildCursors = (value: string) => {
		const interactiveElements = document.querySelectorAll<HTMLElement>(
			"button, a, input, textarea, select",
		);
		for (const el of interactiveElements) {
			if (el.style.cursor !== value) {
				el.style.cursor = value;
			}
		}
	};

	const addListeners = () => {
		element.addEventListener("pointerenter", handlePointerEnter);
		element.addEventListener("pointerleave", handlePointerLeave);
		cameraControls.addEventListener("controlstart", handleControlStart);
		cameraControls.addEventListener("controlend", handleControlEnd);
	};

	const removeListeners = () => {
		element.removeEventListener("pointerenter", handlePointerEnter);
		element.removeEventListener("pointerleave", handlePointerLeave);
		cameraControls.removeEventListener("controlstart", handleControlStart);
		cameraControls.removeEventListener("controlend", handleControlEnd);
		setCursor(document.body, "");
		setChildCursors("");
	};

	addListeners();

	return {
		destroy: removeListeners,
	};
}

/** Options for taking a snapshot. */
export type SnapshotOptions = {
	fileName?: string;
	mimeType?: string;
	/** The size of the snapshot image. */
	size?: {
		/** The width of the snapshot image */
		width: number;
		/** The height of the snapshot image */
		height: number;
	};
	/** A callback function to handle the snapshot image */
	imageHandler?: (
		/** The snapshot image as a Blob */
		image: Blob,
	) => void;
};

export type EventMap = {
	/** Click Event */
	click: {
		/** Properties associated with the object clicked */
		connectedProperties: { name: string; options: string[] }[];
	};
};

class DoppleARIAController {
	container: Element;
	canvas: HTMLCanvasElement;
	projectName!: string;

	ariaContainer!: HTMLDivElement;

	#labelElement!: HTMLDivElement;
	#descriptionElement!: HTMLDivElement;
	#liveRegionContentElement!: HTMLSpanElement;

	constructor({
		container,
		canvas,
		projectName,
	}: { container: Element; canvas: HTMLCanvasElement; projectName: string }) {
		this.container = container;
		this.canvas = canvas;
		this.projectName = projectName;

		this.#init();
	}

	#init() {
		const id = crypto.randomUUID();

		this.canvas.setAttribute("role", "application");
		this.canvas.setAttribute("aria-labelledby", `dopple-aria-label-${id}`);
		this.canvas.setAttribute(
			"aria-describedby",
			`dopple-aria-description-${id}`,
		);
		this.canvas.setAttribute("tabindex", "0");
		this.canvas.style.outline = "none";

		// create aria container
		this.ariaContainer = document.createElement("div");
		this.ariaContainer.style.position = "absolute";
		this.ariaContainer.style.borderWidth = "0";
		this.ariaContainer.style.clip = "rect(0, 0, 0, 0)";
		this.ariaContainer.style.height = "1px";
		this.ariaContainer.style.margin = "-1px";
		this.ariaContainer.style.overflow = "hidden";
		this.ariaContainer.style.padding = "0";
		this.ariaContainer.style.whiteSpace = "nowrap";
		this.ariaContainer.style.width = "1px";

		// create and append label
		this.#labelElement = document.createElement("div");
		this.#labelElement.setAttribute("id", `dopple-aria-label-${id}`);
		this.#labelElement.textContent = `Interactive 3D viewer for ${this.projectName} product`;
		this.ariaContainer.appendChild(this.#labelElement);

		// create and append description
		this.#descriptionElement = document.createElement("div");
		this.#descriptionElement.setAttribute(
			"id",
			`dopple-aria-description-${id}`,
		);
		this.#descriptionElement.textContent =
			"Use the controls in the UI to change this product's configuration. Updates will be announced automatically.";
		this.ariaContainer.appendChild(this.#descriptionElement);

		// create and append live region
		const liveRegion = document.createElement("div");
		liveRegion.setAttribute("role", "region");
		liveRegion.setAttribute("aria-label", "3D scene updates");

		this.#liveRegionContentElement = document.createElement("span");
		this.#liveRegionContentElement.setAttribute(
			"id",
			`dopple-aria-announcer-${id}`,
		);
		this.#liveRegionContentElement.textContent = "";
		this.#liveRegionContentElement.setAttribute("role", "status");
		this.#liveRegionContentElement.setAttribute("aria-atomic", "true");

		liveRegion.appendChild(this.#liveRegionContentElement);

		this.ariaContainer.appendChild(liveRegion);
		this.container.appendChild(this.ariaContainer);
	}

	get label() {
		return this.#labelElement.textContent || "";
	}

	set label(text: string) {
		this.#labelElement.textContent = text;
	}

	get description() {
		return this.#descriptionElement.textContent || "";
	}

	set description(text: string) {
		this.#descriptionElement.textContent = text;
	}

	announce(text: string) {
		this.#liveRegionContentElement.textContent = "";
		void this.#liveRegionContentElement.offsetHeight;

		this.#liveRegionContentElement.textContent = text;
	}

	get liveRegionContent() {
		return this.#liveRegionContentElement.textContent || "";
	}
}

/**
 * The DoppleXR class represents a DoppleXR experience.
 */
export class DoppleXR {
	/**
	 * The owner of the DoppleXR experience.
	 */
	owner!: string;
	/**
	 * The workspace of the DoppleXR experience.
	 */
	workspace!: string;
	/**
	 * The project name of the DoppleXR experience.
	 */
	projectName!: string;
	/**
	 * The product version of the DoppleXR experience.
	 */
	productVersion!: string;
	/**
	 * The logger instance for the DoppleXR experience.
	 */
	logger!: Logger;
	/**
	 * The log namespace for the DoppleXR experience.
	 */
	logNamespace!: string;
	/**
	 * The session identifier for the DoppleXR experience.
	 */
	sessionId!: string;
	/**
	 * The configuration options for the DoppleXR experience.
	 */
	options?: {
		grabCursor?: boolean;
		aria?: boolean;
		keyboardControls?: boolean;
	};

	/**
	 * The ARIA controller for the DoppleXR experience.
	 */
	aria: DoppleARIAController | null = null;
	composer!: EffectComposer;
	renderPass!: RenderPass;
	taaRenderPass!: TAARenderPass;
	renderTarget: WebGLRenderTarget | undefined;

	/**
	 * The DOM element to contain the renderer.
	 */
	container!: Element;

	#matrix!: Matrix;
	contrastPass!: ShaderPass;
	exposurePass!: ShaderPass;
	bloomPass!: UnrealBloomPass;
	/**
	 * The matrix for the DoppleXR experience.
	 */

	get matrix() {
		return this.#matrix;
	}

	/**
	 * The scene instance for the DoppleXR experience.
	 */
	scene!: Scene;
	/**
	 * scenes - A record of scenes for the DoppleXR experience.
	 */
	scenes: Record<keyof Matrix["scenes"], Scene> = {};
	/**
	 * The WebGL renderer for the DoppleXR experience.
	 */
	renderer!: WebGLRenderer;
	#defaultEnvironment!: Texture;
	/**
	 * The environment texture for the DoppleXR experience.
	 */
	environment!: Texture;
	/**
	 * The CSS renderer for the DoppleXR experience.
	 */
	cssRenderer!: CSSRenderer;
	/**
	 * The camera controls for the DoppleXR experience
	 */
	controls!: CameraControls;
	/**
	 * A record of GLTF models for the DoppleXR experience.
	 */
	models: Record<string, GLTF> = {};
	#defaultCamera: PerspectiveCamera | OrthographicCamera =
		new PerspectiveCamera(
			50,
			window.innerWidth / window.innerHeight,
			0.01,
			250,
		);
	/**
	 * The camera instance for the DoppleXR experience.
	 */
	camera = this.#defaultCamera;
	#hotspots: Record<string, CSSObject> = {};
	/**
	 * The raycaster for the DoppleXR experience.
	 */
	raycaster = new Raycaster();
	resetLabels!: () => void;
	#clock = new Clock();
	#mixer: AnimationMixer;
	#animations: Record<string, AnimationAction> = {};
	#xrSession: XRSession | null = null;
	#productGroup: Group;
	#hotspotGroup: Group;
	/**
	 * The loading manager for the DoppleXR experience.
	 */
	loadingManager = new LoadingManager();
	#gltfLoader = new GLTFLoader(this.loadingManager);
	#doppleHDRLoader: DoppleHDRLoader;
	/**
	 * The current selection state.
	 */
	selection!: Selection;
	#dracoLoader = new DRACOLoader();
	#logWriters: Set<LogWriter> = new Set();

	/**
	 * @internal
	 */
	boundingBox = new Box3();
	/**
	 * @internal
	 */
	boundingSphere = new Sphere();
	/**
	 * @internal
	 */
	size = new Vector3();

	#userDragging = false;
	#pauseAutoRotate = false;
	#isAutoRotateEnabled: boolean | undefined;

	#gragCursor:
		| {
				destroy(): void;
		  }
		| undefined;

	#visibilityObserver = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					this.logger.info({ event: "IN_VIEWPORT" }, "in viewport");
				} else {
					this.logger.info({ event: "OUT_OF_VIEWPORT" }, "out of viewport");
				}
			});
		},
		{ threshold: 0 },
	);

	/**
	 * Constructs a new instance of DoppleXR.
	 *
	 * @param config - The configuration object for initializing DoppleXR.
	 * @param config.container - The DOM element to contain the renderers.
	 * @param config.scene - Optional, a pre-existing scene to use.
	 * @param config.renderer - Optional, a pre-existing WebGL renderer to use.
	 * @param config.cssRenderer - Optional, a pre-existing CSS renderer to use.
	 * @param config.selection - The current selection state.
	 * @param config.owner - The owner identifier.
	 * @param config.workspace - The workspace identifier.
	 * @param config.projectName - Optional, the project name.
	 * @param config.productVersion - Optional, the product version.
	 * @param config.logNamespace - The namespace for logging.
	 * @param config.sessionId - The session identifier.
	 * @param config.options - Optional, configuration flags.
	 * @param config.options.grabCursor - Optional, Enables/disables cursor grabbing. Defaults to `true`.
	 */
	constructor(config: {
		container: Element;
		scene?: Scene;
		renderer?: WebGLRenderer;
		cssRenderer?: CSSRenderer;
		selection: Selection;
		owner: string;
		workspace: string;
		projectName?: string;
		productVersion?: string;
		logNamespace: string;
		sessionId: string;
		options?: {
			grabCursor?: boolean;
			aria?: boolean;
			keyboardControls?: boolean;
		};
	}) {
		Object.assign(this, config);
		this.options = {
			grabCursor: true,
			aria: true,
			keyboardControls: true,
			...(config.options || {}),
		};

		this.logger = log.child({
			namespace: this.logNamespace,
			owner: config.owner,
			workspace: config.workspace,
			project: this.projectName,
			version: this.productVersion,
			sessionId: this.sessionId,
		});

		this.scene ??= new Scene();

		this.renderer ??= new WebGLRenderer({
			antialias: true,
			alpha: true,
			logarithmicDepthBuffer: false,
			preserveDrawingBuffer: true,
		});
		this.renderer.sortObjects = true;

		this.#initialisePostProcessing();

		const pmremGenerator = new PMREMGenerator(this.renderer);

		this.#defaultEnvironment = pmremGenerator.fromScene(
			new RoomEnvironment(),
		).texture;

		pmremGenerator.dispose();

		this.cssRenderer ??= new CSSRenderer();

		this.container.innerHTML = "";
		this.container.appendChild(this.renderer.domElement);
		this.renderer.domElement.style.position = "absolute";
		this.renderer.domElement.style.top = "0";
		this.renderer.domElement.style.zIndex = "0";
		this.container.appendChild(this.cssRenderer.domElement);
		this.cssRenderer.domElement.style.position = "absolute";
		this.cssRenderer.domElement.style.top = "0";
		this.cssRenderer.domElement.style.zIndex = "1";

		Object.assign(this.raycaster, { firstHitOnly: true });

		this.resetLabels = throttle(this.#resetLabels.bind(this), 100);

		this.scene.environment = this.#defaultEnvironment;
		this.#productGroup = new Group();
		this.#productGroup.name = "gltfSceneGroup";
		this.scene.add(this.#productGroup);

		this.#hotspotGroup = new Group();
		this.#hotspotGroup.name = "hotspotGroup";

		this.scene.add(this.#hotspotGroup);

		this.#mixer = new AnimationMixer(this.scene);

		this.#doppleHDRLoader = new DoppleHDRLoader();

		if (this.options?.aria) {
			this.aria = new DoppleARIAController({
				container: this.container,
				canvas: this.renderer.domElement,
				projectName: this.projectName,
			});
		}

		const ktx2Loader = new KTX2Loader(this.loadingManager)
			.setTranscoderPath(
				`https://unpkg.com/three@0.${REVISION}.x/examples/jsm/libs/basis/`,
			)
			.detectSupport(this.renderer);

		this.#gltfLoader.setKTX2Loader(ktx2Loader);

		this.#gltfLoader.register(
			(parser) =>
				new GLTFMaterialEnvironmentExtension(parser, this.#doppleHDRLoader),
		);

		this.#gltfLoader.register((parser) => new DPLNodeLocatorExtension(parser));

		this.#dracoLoader.setDecoderPath(
			"https://www.gstatic.com/draco/versioned/decoders/1.4.3/",
		);

		this.#gltfLoader.setDRACOLoader(this.#dracoLoader);

		transportLayers.set(this, this.#logWriters);

		this.cssRenderer.domElement.addEventListener("mousedown", (event) => {
			const mouse = new Vector2();
			const rect = this.cssRenderer.domElement.getBoundingClientRect();
			mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
			mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

			this.raycaster.setFromCamera(mouse, this.camera);

			const intersects = this.raycaster.intersectObjects(
				this.#productGroup.children,
			);

			if (!intersects[0]) {
				for (const listener of this.#eventListeners.click) {
					listener({ connectedProperties: [] });
				}
				return;
			}

			const mesh = intersects[0].object;

			const [sceneId, model] =
				Object.entries(this.models).find(([_, { scene }]) =>
					scene.getObjectById(mesh.id),
				) || [];

			if (!(sceneId && model)) {
				for (const listener of this.#eventListeners.click) {
					listener({ connectedProperties: [] });
				}
				return;
			}

			const properties: Record<string, Set<string>> = {};

			const nodeConfig = this.matrix.scenes[sceneId].nodes?.[mesh.name];

			if (nodeConfig) {
				for (const [property, options] of Object.entries(
					nodeConfig.visibility,
				)) {
					if (!options) {
						continue;
					}

					properties[property] ??= new Set();

					for (const option of Object.keys(options)) {
						properties[property].add(option);
					}
				}
			}

			const gltfJSON = model.parser.json as GLTFTypes.IGLTF;
			const gltfNode = gltfJSON.nodes?.find((n) => n.name === mesh.name);

			const variantsExt = gltfJSON.extensions?.KHR_materials_variants as {
				variants: { name: string }[];
			};

			if (gltfNode?.mesh && variantsExt) {
				const primitives = gltfJSON.meshes?.[gltfNode.mesh].primitives;

				for (const primitive of primitives || []) {
					const primitiveVariantsExt = primitive.extensions
						?.KHR_materials_variants as
						| {
								mappings: { variants: [] }[];
						  }
						| undefined;

					if (!primitiveVariantsExt) {
						continue;
					}

					for (const mapping of primitiveVariantsExt.mappings) {
						for (const variantIndex of mapping.variants) {
							const variantName = variantsExt.variants[variantIndex]?.name;

							const variantConfig =
								this.matrix.scenes[sceneId].variants?.[variantName];

							if (!variantConfig) {
								continue;
							}

							for (const [property, options] of Object.entries(
								variantConfig.selected,
							)) {
								if (!options) {
									continue;
								}

								properties[property] ??= new Set();

								for (const option of Object.keys(options)) {
									properties[property].add(option);
								}
							}
						}
					}
				}
			}

			for (const listener of this.#eventListeners.click) {
				listener({
					connectedProperties: Object.entries(properties).map(
						([name, options]) => ({ name, options: Array.from(options) }),
					),
				});
			}
		});

		this.#visibilityObserver.observe(this.container);
	}

	/**
	 * Adds a log writer to the set of log writers.
	 *
	 * @param writer - The log writer to be added.
	 */

	addLogWriter(writer: LogWriter) {
		this.#logWriters.add(writer);
	}

	/**
	 * Removes all log writers from the set of log writers.
	 *
	 * Use this when you want to stop sending logs to all log writers.
	 */
	removeLogWriter(writer: LogWriter) {
		this.#logWriters.delete(writer);
	}

	#initialisePostProcessing() {
		// setup composer
		this.composer = new EffectComposer(this.renderer, this.renderTarget);

		// Set up render pass
		this.renderPass = new RenderPass(this.scene, this.camera);
		this.composer.addPass(this.renderPass);

		// initialise taa with default sample level 2
		this.taaRenderPass = new TAARenderPass(this.scene, this.camera);
		this.taaRenderPass.unbiased = false;
		this.taaRenderPass.sampleLevel = 2;
		this.composer.addPass(this.taaRenderPass);

		//initialize ShaderPass for brightness, contrast and exposure
		// Brightness/Contrast Pass
		this.contrastPass = new ShaderPass(BrightnessContrastShader);
		this.contrastPass.uniforms.brightness.value = 0.0;
		this.contrastPass.uniforms.contrast.value = 0.0;
		this.composer.addPass(this.contrastPass);

		// Exposure Pass
		this.exposurePass = new ShaderPass(ExposureShader);
		this.exposurePass.uniforms.exposure.value = 1.0;
		this.composer.addPass(this.exposurePass);

		// Bloom Pass
		this.bloomPass = new UnrealBloomPass(
			new Vector2(this.container.clientWidth, this.container.clientHeight), // resolution
			1.0, // strength
			0.0, // radius
			0.8, // threshold
		);

		this.bloomPass.enabled = false;
		this.composer.addPass(this.bloomPass);

		// set outpass
		const outputPass = new OutputPass();
		this.composer.addPass(outputPass);
	}

	#updateMSAA(sampleRate: number) {
		if (sampleRate === -1) {
			if (this.renderTarget) {
				this.renderTarget.dispose();
				this.renderTarget = undefined;
			} else {
				return;
			}
		} else {
			const { clientWidth, clientHeight } = this.container;
			this.renderTarget = new WebGLRenderTarget(clientWidth, clientHeight, {
				samples: sampleRate,
				type: FloatType,
			});
		}

		this.composer.dispose();
		this.composer = new EffectComposer(this.renderer, this.renderTarget);
		this.composer.addPass(this.renderPass);

		this.composer.addPass(this.taaRenderPass);

		this.composer.addPass(this.contrastPass);
		this.composer.addPass(this.exposurePass);
		this.composer.addPass(this.bloomPass);

		// set outpass
		const outputPass = new OutputPass();
		this.composer.addPass(outputPass);
	}

	#initializeAutoRotatePauseHandlers() {
		const onRest = () => {
			this.controls.removeEventListener("rest", onRest);
			this.#userDragging = false;
			this.#pauseAutoRotate = false;
		};

		this.controls.addEventListener("controlstart", () => {
			this.controls.removeEventListener("rest", onRest);
			this.#userDragging = true;
			this.#pauseAutoRotate = true;
		});

		this.controls.addEventListener("controlend", () => {
			if (this.controls.active) {
				this.controls.addEventListener("rest", onRest);
			} else {
				onRest();
			}
		});

		this.controls.addEventListener("transitionstart", () => {
			if (this.#userDragging) {
				return;
			}

			this.#pauseAutoRotate = true;
			this.controls.addEventListener("rest", onRest);
		});
	}

	async #loadScene(product: (typeof this.matrix.scenes)[string]) {
		const baseURLRes = await getProductBaseURL(this);
		if (baseURLRes.isErr()) {
			return err(baseURLRes.error);
		}
		const productName = product.modelName;
		const path = product.path;
		const { mediaPath } = baseURLRes.value;

		this.#gltfLoader.setPath(`${mediaPath}/${path}/`);
		const gltfRes = await ResultAsync.fromPromise(
			this.#gltfLoader.loadAsync(productName),
			(err) => new Error(`Error loading gltf "${productName}"`, { cause: err }),
		);

		if (gltfRes.isOk()) {
			const gltf = gltfRes.value;

			gltf.scene.position.set(...product.positioning.default.translation);
			gltf.scene.scale.set(...product.positioning.default.scale);
			gltf.scene.rotation.set(...product.positioning.default.rotation);

			if (gltf.animations) {
				for (const animation in gltf.animations) {
					gltf.animations[animation].name ??= `Animation ${animation}`;
				}
			}

			gltf.scene.traverse((obj) => {
				if (obj instanceof Mesh) {
					obj.geometry.boundsTree ??= new MeshBVH(obj.geometry);
				}
			});
		}

		return gltfRes;
	}

	#logCamera = debounce(() => {
		const target = new Vector3();

		this.logger.info(
			{
				event: "CAMERA_UPDATE",
				position: this.camera.position.toArray(),
				target: this.controls.getTarget(target).toArray(),
			},
			"camera update",
		);
	}, 250);

	/**
	 * Loads the product.
	 *
	 * This method is called when the {@link DoppleXR} instance is created.
	 * It loads the product's matrix and sets up the camera, controls and renderer.
	 *
	 * @param loadOptions - Optional, configuration for loading.
	 * @param loadOptions.trackParentSize - Optional, whether to auto resize the dopple element when container element gets resized. Defaults to `true`.
	 * @returns A promise that is resolved when the product is loaded.
	 */
	async load({ trackParentSize = true } = {}): Promise<Result<void, Error>> {
		const startTime = Date.now();
		this.logger.info({ event: "INIT" }, "Loading product");
		const baseURLRes = await getProductBaseURL(this);
		if (baseURLRes.isErr()) {
			return err(
				new Error("Unable to load project", { cause: baseURLRes.error }),
			);
		}

		const { mediaPath, searchParams } = baseURLRes.value;

		const url = new URL(mediaPath);
		for (const key in searchParams) {
			url.searchParams.set(key, searchParams[key]);
		}

		this.loadingManager.setURLModifier((oldURL) => {
			const newURL = new URL(oldURL);

			newURL.pathname = newURL.pathname
				.split("/")
				.map((segment) => encodeURIComponent(decodeURIComponent(segment)))
				.join("/");

			return `${newURL}${url.search}`;
		});

		const matrixRes = await loadFile({
			owner: this.owner,
			workspace: this.workspace,
			projectName: this.projectName,
			productVersion: this.productVersion,
			filePath: "matrix.json",
		});

		this.logger.info({ event: "LOADED_MATRIX" }, "Loaded product's matrix");

		if (matrixRes.isErr()) {
			return err(
				new Error("Unable to load project", { cause: matrixRes.error }),
			);
		}

		const matrix = matrixRes.value as Matrix;
		matrix.settings ??= {};
		{
			const dopplexr = this;
			const proxiedSettings = new Proxy(matrix.settings, {
				set(obj, prop, value) {
					if (prop === "backgroundColor") {
						// Check if value is a valid hex format: #RRGGBB or #RRGGBBAA
						const isValidHex = /^#([0-9A-Fa-f]{6})([0-9A-Fa-f]{2})?$/.test(
							value,
						);
						if (isValidHex) {
							const hexColor = new Color(value.slice(0, 7));
							const alpha = +(
								Number.parseInt(value.slice(7, 9).padEnd(2, "FF"), 16) / 255
							).toFixed(3);
							dopplexr.taaRenderPass.clearColor = hexColor;
							dopplexr.taaRenderPass.clearAlpha = alpha;
							dopplexr.renderer.setClearColor(
								hexColor.clone().convertSRGBToLinear(),
								alpha,
							);
						} else {
							console.warn(
								`Invalid backgroundColor value: "${value}". Expected format #RRGGBB or #RRGGBBAA.`,
							);
						}
					}

					(obj as Record<string, unknown>)[prop as string] = value;

					return true;
				},
			});

			Object.defineProperty(matrix, "settings", {
				get() {
					return proxiedSettings;
				},
				configurable: false,
				enumerable: true,
			});
		}
		this.#matrix = Object.seal(matrix);

		this.camera.position.set(0, 1.6, 3);
		this.camera.updateProjectionMatrix();

		this.controls = new CameraControls(
			this.camera,
			this.cssRenderer.domElement,
		);
		if (this.options?.grabCursor) {
			this.#gragCursor = createGrabCursor(
				this.cssRenderer.domElement,
				this.controls,
			);
		}

		this.controls.addEventListener("update", () => {
			updateCameraClippingPlanes(this, this.controls.camera);
			this.resetLabels();
			this.#logCamera();
		});

		this.#initializeAutoRotatePauseHandlers();

		this.#resetControls();

		if (this.options?.keyboardControls) {
			const keyboardControl = new KeyboardControl({
				cameraControls: this.controls,
			});

			this.container.addEventListener("click", () => {
				this.renderer.domElement.focus();
			});

			this.renderer.domElement.addEventListener("focus", () => {
				keyboardControl.initialize();
			});

			this.renderer.domElement.addEventListener("blur", () => {
				keyboardControl.dispose();
			});
		}

		this.resize({ trackParentSize });

		const doppleParam = new URL(location.href).searchParams.get("dopple");

		let doppleData = null;
		if (doppleParam) {
			try {
				doppleData = JSON.parse(doppleParam);
			} catch (e) {
				this.logger.warn(
					{ error: e instanceof Error ? e.message : String(e) },
					"DOPPLE_PARAM_PARSE_FAILED",
				);
			}
		}

		let updated: Result<void, Error>;
		if (doppleData) {
			updated = await this.updateSelection(doppleData?.selection);
		} else {
			updated = await this.updateSelection();
		}

		if (updated?.isErr()) {
			return updated;
		}

		if (doppleData?.arMode === true) {
			if (
				navigator.xr &&
				typeof navigator.xr.isSessionSupported === "function" &&
				(await navigator.xr.isSessionSupported("immersive-ar"))
			) {
				if (
					confirm(
						"This page wants to enter AR mode. Would you like to continue?",
					)
				) {
					this.startAR().catch((e) => this.logger.warn("START_XR_FAILED", e));
				}
			} else {
				this.startAR().catch((e) => this.logger.warn("START_AR_FAILED", e));
			}
		}

		const duration = Date.now() - startTime;
		this.logger.info({ event: "LOADED", duration }, "Loaded");

		return ok(undefined);
	}

	async #startXR() {
		if (this.#xrSession) {
			throw new Error("Already started AR");
		}

		if (!navigator.xr) {
			throw new Error("No XR");
		}

		if (!navigator.xr?.isSessionSupported) {
			throw new Error("No Webxr");
		}

		if (!XRSession.prototype.requestHitTestSource) {
			throw new Error("No Webxr Hit Test");
		}

		this.renderer.xr.enabled = true;

		const { ARRenderer } = await import("./ar/ARRenderer.ts");
		const arRenderer = new ARRenderer(this);

		const renderLoop = async (time: number, frame: XRFrame) => {
			if (!frame) {
				return;
			}

			arRenderer.onWebXRFrame(time, frame);
		};

		this.renderer.setAnimationLoop(renderLoop);

		await arRenderer.present(this.scene);
	}

	/**
	 * Starts the AR experience.
	 *
	 * If the device is a mobile device with WebXR support, this method will start
	 * the AR experience using the WebXR API. Otherwise, it will generate a QR code
	 * that can be scanned to open the AR experience on a mobile device.
	 *
	 * If the device is an Android device without WebXR support, this method will
	 * return an empty string.
	 *
	 * @param options - Configuration options for the URL generation.
	 * @param options.baseUrl - The base URL to use for the shareable link.
	 * @returns A string containing the QR code data URL, or an empty string if the
	 * device is an Android device without WebXR support.
	 */
	async startAR(options: { baseUrl?: string } = {}): Promise<string> {
		const isMobile =
			/iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ||
			(navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
		const isAndroidMobile = /Android/i.test(navigator.userAgent);

		this.logger.info({ event: "AR_INITIATED" }, "AR was initiated");

		if (isMobile) {
			if (
				navigator.xr &&
				(await navigator.xr.isSessionSupported("immersive-ar"))
			) {
				await this.#startXR();
				return "";
			}

			if (!isAndroidMobile) {
				const { USDZExporter } = await import(
					"three/addons/exporters/USDZExporter.js"
				);
				const exporter = new USDZExporter();
				exporter.setTextureUtils(WebGLTextureUtils);

				const arraybuffer = await exporter.parseAsync(this.scene);

				const blob = new Blob([arraybuffer], {
					type: "application/octet-stream",
				});
				const link = URL.createObjectURL(blob);

				const anchor = document.createElement("a");
				anchor.setAttribute("rel", "ar");
				const img = document.createElement("img");
				anchor.appendChild(img);
				anchor.setAttribute("href", link);
				anchor.setAttribute("download", "model.usdz");

				this.container?.appendChild(anchor);
				anchor.click();
				anchor.removeChild(img);

				return "";
			}
		}
		const baseUrl = options?.baseUrl || import.meta.env.VITE_APP_SHARE_BASE_URL;
		const shareData = await this.generateShareURL({
			baseUrl,
			qrCode: true,
			arMode: true,
		});

		return shareData.qrCode ?? "";
	}

	/**
	 * Generates a shareable URL for the AR experience.
	 *
	 * This method constructs a URL containing necessary parameters for the AR session
	 * and optionally generates a QR code for easy sharing.
	 *
	 * @param options - Configuration options for the URL generation.
	 * @param options.baseUrl - The base URL to use for the shareable link.
	 * @param options.arMode - Whether AR mode should be enabled in the link.
	 * @param options.qrCode - Whether to generate a QR code for the link.
	 * @returns An object containing the shareable URL and an optional QR code data URL.
	 */
	async generateShareURL(
		options: { baseUrl?: string; arMode?: boolean; qrCode?: boolean } = {},
	): Promise<{
		url: string;
		qrCode: string | null;
	}> {
		const { baseUrl, arMode = false, qrCode = false } = options;

		let urlObj: URL;
		try {
			const defaultUrl = window.location.href.split("?")[0];
			urlObj = new URL(baseUrl || defaultUrl);
		} catch (_e) {
			throw new Error("Invalid base URL provided");
		}

		const doppleParams = {
			arMode,
			owner: this.owner,
			workspace: this.workspace,
			product: this.projectName,
			version: this.productVersion,
			selection: this.selection,
		};

		urlObj.searchParams.set("dopple", JSON.stringify(doppleParams));

		const url = urlObj.toString();

		let qrCodeData: string | null = null;
		if (qrCode) {
			try {
				qrCodeData = await QRCode.toDataURL(url);
			} catch (_error) {
				console.warn("Generated URL is too long for a QR code to handle");
				qrCodeData = null;
			}
		}

		return {
			url,
			qrCode: qrCodeData,
		};
	}

	/**
	 * Takes a snapshot of the current scene.
	 *
	 * @param options - Optional options.
	 * @param options.fileName - The file name of the snapshot. Defaults to
	 * `${this.projectName}_snapshot`.
	 * @param options.mimeType - The MIME type of the snapshot image. Defaults to
	 * "image/png".
	 * @param options.size - The size of the snapshot image. If specified, the
	 * renderer's size will be set to this size before taking the snapshot.
	 * @param options.imageHandler - A callback function to handle the snapshot
	 * image. The function will be called with the snapshot image as a Blob.
	 *
	 * @returns A Promise that resolves when the snapshot has been taken.
	 */
	async takeSnapshot(options?: SnapshotOptions) {
		const fileName = options?.fileName || `${this.projectName}_snapshot`;
		const mimeType = options?.mimeType || "image/png";

		const originalWidth = this.renderer.domElement.width;
		const originalHeight = this.renderer.domElement.height;
		const originalAspect = (this.camera as PerspectiveCamera).aspect;

		if (options?.size) {
			const { width, height } = options.size;
			this.renderer.setSize(width, height, false);

			(this.camera as PerspectiveCamera).aspect = width / height;
			this.camera.updateProjectionMatrix();

			this.renderer.render(this.scene, this.camera);
		}

		const blobImage = await new Promise((resolve) =>
			this.renderer.domElement.toBlob(resolve, mimeType),
		);

		if (options?.imageHandler) {
			options.imageHandler(blobImage as Blob);
		} else {
			const link = URL.createObjectURL(blobImage as Blob);

			const anchor = document.createElement("a");
			anchor.setAttribute("rel", "snapshot");
			anchor.setAttribute("href", link);
			anchor.setAttribute("download", fileName);

			this.container.appendChild(anchor);
			anchor.click();

			this.logger.info({ event: "SNAPSHOT_TAKEN" }, "Snapshot Taken");

			this.container.removeChild(anchor);
		}

		this.renderer.setSize(originalWidth, originalHeight, false);

		(this.camera as PerspectiveCamera).aspect = originalAspect;
		this.camera.updateProjectionMatrix();

		this.renderer.render(this.scene, this.camera);
	}

	/**
	 * Stops the AR experience.
	 *
	 * This method will stop the AR experience if it is currently running.
	 *
	 * @returns A Promise that resolves when the AR experience has been stopped.
	 */
	async stopAR() {
		this.renderer.setAnimationLoop(null);
		this.#xrSession?.end();

		this.#xrSession = null;
	}

	async #resetControls() {
		this.camera.layers.enableAll();
		this.camera.layers.toggle(1);
		const extras = this.camera.userData.extras as CameraExtras;

		if (this.camera instanceof PerspectiveCamera) {
			this.camera.aspect =
				this.container.clientWidth / this.container.clientHeight;
		}
		this.controls.camera = this.camera;
		this.renderPass.camera = this.camera;

		this.controls.minDistance = getValueOrDefault(
			extras?.minDistance,
			Number.EPSILON,
		);
		this.controls.maxDistance = getValueOrDefault(
			extras?.maxDistance,
			Number.POSITIVE_INFINITY,
		);
		this.controls.minPolarAngle = getValueOrDefault(extras?.minPolarAngle, 0);
		this.controls.maxPolarAngle = getValueOrDefault(
			extras?.maxPolarAngle,
			Math.PI,
		);

		this.controls.minAzimuthAngle = getValueOrDefault(
			extras?.minAzimuthAngle,
			Number.NEGATIVE_INFINITY,
		);
		this.controls.maxAzimuthAngle = getValueOrDefault(
			extras?.maxAzimuthAngle,
			Number.POSITIVE_INFINITY,
		);

		this.controls.smoothTime = extras?.smoothTime ?? 0.25;
		this.controls.dollySpeed = extras?.dollySpeed ?? 1.0;
		this.controls.truckSpeed = extras?.truckSpeed ?? 2.0;

		this.camera.userData.autoRotateSpeed = extras?.autoRotateSpeed || 0;

		const position = new Vector3(0, 1.6, 3);
		this.camera?.getWorldPosition(position);

		const quaternion = new Quaternion();
		this.camera?.getWorldQuaternion(quaternion);

		if (extras?.target) {
			this.controls.setLookAt(
				position?.x,
				position?.y,
				position?.z,
				extras.target?.x,
				extras.target?.y,
				extras.target?.z,
				true,
			);
		} else {
			const direction = new Vector3(0, 0, -1);
			direction.applyQuaternion(quaternion);

			const t = -position.dot(direction) / direction.lengthSq();
			const closestPoint = position
				.clone()
				.add(direction.clone().multiplyScalar(t));

			const pointLookingAt = new Vector3(
				Math.round(closestPoint.x * 1000) / 1000,
				Math.round(closestPoint.y * 1000) / 1000,
				Math.round(closestPoint.z * 1000) / 1000,
			);
			this.controls.setLookAt(
				position.x,
				position.y,
				position.z,
				pointLookingAt.x,
				pointLookingAt.y,
				pointLookingAt.z,
				true,
			);
		}
		this.controls.saveState();
		this.taaRenderPass.camera = this.camera;
	}

	/**
	 * Update the selection of products, cameras, nodes, variants, hotspots and environments
	 */
	updateMatrix = this.updateSelection.bind(this);

	#processingSelection: Promise<void> | null = null;

	/**
	 * Update the selection of products, cameras, nodes, variants, hotspots and environments
	 * based on the given selection and matrix.
	 *
	 * @param {Object<string, string>} [newSelection] - an object with choice IDs as keys and
	 *   option IDs as values.
	 * @param {Object<string, any>} [matrix] - the matrix to use for selection. If not provided,
	 *   the current matrix will be used.
	 * @param {Object<string, any>} [options] - options object with the following properties:
	 *   - `disableHotspots`: if true, hotspots will not be updated.
	 *
	 * @returns {Promise<Result<void, Error>>}
	 */
	async updateSelection(
		newSelection?: Selection,
		matrix?: Matrix,
		{ disableHotspots }: { disableHotspots?: boolean } = {},
	): Promise<Result<void, Error>> {
		// Wait for previous selection update calls
		while (this.#processingSelection) {
			await this.#processingSelection.catch(() => {});
		}

		const res = this.#updateSelection(newSelection, matrix, {
			disableHotspots,
		});

		this.#processingSelection = res.then(async () => {
			const upscalePromises = Object.values(this.models).map((model) =>
				upscalePendingTextures(model.parser, model.scene),
			);

			// Upscale Textures - errors can be ignored
			const res = await ResultAsync.combine(upscalePromises);

			if (res.isErr()) {
				this.logger.info(
					{
						event: "TEXTURE_UPSCALING_FAILED",
					},
					res.error.message,
				);

				this.#processingSelection = null;
				return;
			}

			const endTime = Date.now();

			let startTime = endTime;

			const texturesUpscaled = res.value.reduce(
				(acc, { startTime: modelStartTime, texturesUpscaled }) => {
					startTime = Math.min(startTime, modelStartTime);
					return acc + texturesUpscaled;
				},
				0,
			);

			this.logger.info(
				{
					event: "TEXTURE_UPSCALED",
					texturesUpscaled,
					duration: endTime - startTime,
				},
				"Texture Upscaled",
			);

			this.#processingSelection = null;
		});

		return res;
	}

	async #updateSelection(
		newSelection?: Selection,
		matrix?: Matrix,
		{ disableHotspots }: { disableHotspots?: boolean } = {},
	) {
		const matrixJson = matrix ?? this.matrix;

		const oldSelection = structuredClone(this.selection);
		const selection = newSelection
			? { ...this.selection, ...newSelection }
			: this.selection;

		for (const [choiceId, { options }] of Object.entries(matrixJson.choices)) {
			const possibleSelections = Object.entries(options).map(
				([optionId, optionValue]) => optionValue || optionId,
			);

			if (!possibleSelections.includes(selection[choiceId])) {
				this.logger.warn(
					{ choiceId },
					`Missing selection for choice ${choiceId}`,
				);
				selection[choiceId] = possibleSelections[0];
			}
		}

		this.selection = selection;

		const selectionMap = new Map<string, string>();

		for (const choice in matrixJson.choices) {
			selectionMap.set(
				choice,
				Object.keys(matrixJson.choices[choice].options).find(
					(option) =>
						(matrixJson.choices[choice].options[option] || option) ===
						selection[choice],
				) ?? "",
			);
		}
		this.logger.info(
			{ event: "UPDATE_SELECTION", selection, oldSelection },
			"Updating Selection",
		);

		let selectedCamera = this.#getDefaultCamera();

		const loadScenes = Result.combineWithAllErrors(
			await mapAsync(
				Object.entries(matrixJson.scenes),
				async ([sceneId, sceneConfig]) => {
					const isSceneVisible = Object.keys(selection).every((choice) => {
						return (
							sceneConfig.visibility[choice]?.[
								selectionMap.get(choice) as string
							] ?? true
						);
					});

					if (!isSceneVisible) {
						return ok(false);
					}

					if (sceneId in this.models) {
						return ok(true);
					}

					const gltfRes = await this.#loadScene({
						...sceneConfig,
						name: sceneId,
					});

					return gltfRes.map((gltf) => {
						this.models[sceneId] = gltf;
						this.scenes[sceneId] = new Scene();
						return true;
					});
				},
			),
		);

		if (loadScenes.isErr()) {
			return err(
				new Error("Error loading scenes", { cause: loadScenes.error }),
			);
		}

		const state: {
			scenes: {
				[sceneId: string]: {
					visibility: boolean;
					positioning: { [positioningId: string]: { selected: boolean } };
					cameras: { [cameraId: string]: { visibility: boolean } };
					nodes: { [nodeId: string]: { visibility: boolean } };
					variants: { [variantId: string]: { selected: boolean } };
					animations: { [animationId: string]: { play: boolean } };
				};
			};
			environments: { [environmentId: string]: { selected: boolean } };
			hotspots: {
				[hotspotId: string]: { visibility: boolean };
			};
		} = {
			scenes: {},
			hotspots: {},
			environments: {},
		};

		const variantPromises: Promise<void>[] = [];

		for (const [sceneId, sceneConfig] of Object.entries(matrixJson.scenes)) {
			const scene = this.scenes[sceneId];
			const gltf = this.models[sceneId];

			const isProductVisible = Object.keys(selection).every((choice) => {
				return (
					sceneConfig.visibility[choice]?.[
						selectionMap.get(choice) as string
					] ?? true
				);
			});

			state.scenes[sceneId] = {
				visibility: isProductVisible,
				positioning: {},
				cameras: {},
				nodes: {},
				variants: {},
				animations: {},
			};

			if (!isProductVisible) {
				gltf?.scene.traverse((obj) => {
					if (obj instanceof Mesh) {
						disposeObject(obj);
					}
				});
				gltf?.scene.removeFromParent();
				scene?.remove(gltf?.scene);
				continue;
			}

			const gltfJSON = gltf.parser.json as GLTFTypes.IGLTF;

			if (!this.#productGroup.children.includes(gltf.scene)) {
				scene.add(gltf.scene);
				this.#productGroup.add(gltf.scene);
			}
			scene.environment = this.#defaultEnvironment;

			for (const nodeName in sceneConfig.nodes) {
				const node = sceneConfig.nodes[nodeName];
				const nodeIndex =
					gltfJSON.nodes?.findIndex(({ name }) => name === nodeName) ?? -1;

				if (nodeIndex === -1) {
					console.warn(
						new Error(`Invalid node ${nodeName} configured in matrix`),
					);

					continue;
				}

				if (!node?.visibility) {
					continue;
				}

				const isVisible = Object.keys(selection).every((choice) => {
					return (
						node.visibility[choice]?.[selectionMap.get(choice) as string] ??
						true
					);
				});

				state.scenes[sceneId].nodes[nodeName] = { visibility: isVisible };

				if (isVisible) {
					const res = await loadNode(
						nodeIndex,
						gltf,
						state.scenes[sceneId].nodes,
					);
					if (res.isErr()) {
						return err(new Error(res.error.message, { cause: res.error }));
					}
				} else {
					const meshNode = gltf.scene.getObjectByName(
						PropertyBinding.sanitizeNodeName(nodeName),
					);
					if (meshNode) {
						disposeObject(meshNode);
					}
					meshNode?.removeFromParent();
				}
			}

			for (const positioningId in sceneConfig.positioning) {
				const { translation, scale, rotation, selected, locatorNode } =
					sceneConfig.positioning[positioningId];

				const isSelected = Object.keys(selection).every((choice) => {
					return selected[choice]?.[selectionMap.get(choice) as string] ?? true;
				});

				state.scenes[sceneId].positioning[positioningId] = {
					selected: isSelected,
				};

				if (isSelected) {
					const group = gltf.scene;
					if (locatorNode) {
						const locator = this.models[
							locatorNode.sceneId
						].scene.getObjectByName(locatorNode.nodeName);
						if (locator) {
							locator.add(group);
							group.position.set(0, 0, 0);
							group.scale.set(1, 1, 1);
							group.rotation.set(0, 0, 0);
							group.updateMatrixWorld(true);
						}
					} else {
						group.position.set(...translation);
						group.scale.set(...scale);
						group.rotation.set(...rotation);
						group.updateMatrixWorld(true);
					}
					break;
				}
			}

			for (const cameraName in sceneConfig.cameras) {
				const { visibility } = sceneConfig.cameras[cameraName];

				let isVisible: boolean | undefined;
				for (const choice of Object.keys(visibility)) {
					const value =
						visibility[choice]?.[selectionMap.get(choice) as string];
					if (value === false) {
						isVisible = value;
						break;
					}

					isVisible ??= value;
				}

				state.scenes[sceneId].cameras[cameraName] = {
					visibility: isVisible ?? false,
				};

				if (isVisible) {
					const cameraIndex =
						gltfJSON.cameras?.findIndex(({ name: n }) => n === cameraName) ??
						-1;

					if (cameraIndex === -1) {
						break;
					}
					const cameraKey = `${sceneId}/${cameraName}`;

					if (selectedCamera.userData.cameraKey === cameraKey) {
						break;
					}

					const camera = await gltf.parser.getDependency("camera", cameraIndex);

					let extras = {} as CameraExtras;
					if (gltfJSON.cameras) {
						extras = gltfJSON.cameras[cameraIndex].extras as CameraExtras;
					}

					selectedCamera = camera.clone();
					selectedCamera.userData.cameraKey = cameraKey;
					selectedCamera.userData.extras = extras;
					break;
				}
			}

			const rootVariantDefs =
				(
					gltfJSON.extensions?.KHR_materials_variants as {
						variants: { name?: string }[];
					}
				)?.variants ?? [];

			const promises = (gltfJSON.meshes || [])?.map(
				async (glTFMesh, meshIndex) => {
					for (const [
						primitiveIndex,
						glTFPrim,
					] of glTFMesh.primitives.entries()) {
						let meshObject = await (
							gltf.parser as unknown as { cache: Map<string, Promise<Mesh>> }
						).cache.get(`mesh:${meshIndex}`);

						if (!meshObject) {
							continue;
						}

						if (glTFMesh.primitives.length > 1) {
							meshObject = meshObject.children
								.filter((m) => m instanceof Mesh)
								.at(primitiveIndex);
						}

						if (!meshObject) {
							continue;
						}

						if (!meshObject.userData.originalMaterial) {
							meshObject.userData.originalMaterial = meshObject.material;
						}

						const mappingDefs =
							(
								glTFPrim.extensions?.KHR_materials_variants as {
									mappings: {
										material: number;
										variants: number[];
										extras?: {
											group?: string;
										};
									}[];
								}
							)?.mappings ?? [];

						let material = meshObject.userData.originalMaterial;
						let selectedMapping = null;
						let selectedVariant = null;

						mapping: for (const mappingDef of mappingDefs) {
							for (const variantIndex of mappingDef.variants) {
								const variantName = rootVariantDefs[variantIndex]?.name;

								if (!variantName) {
									continue;
								}

								const variantConfig = sceneConfig.variants[variantName];
								if (!variantConfig?.selected) {
									continue;
								}

								const groupName = mappingDef.extras?.group ?? "ungrouped";

								const variantGroupName = `${glTFMesh.name}_${primitiveIndex}:${groupName}`;

								const variantGroupConfig =
									sceneConfig.variantGroups?.[variantGroupName];

								let isSelected = 0;

								for (const choice in selection) {
									const selected =
										variantConfig.selected[choice]?.[
											selectionMap.get(choice) as string
										];

									if (selected === false) {
										isSelected = 0;
										break;
									}

									const groupSelected =
										variantGroupConfig?.selected[choice]?.[
											selectionMap.get(choice) as string
										];

									if (groupSelected === false) {
										isSelected = 0;
										break;
									}

									if (isSelected === 1) {
										continue;
									}

									if (selected) {
										isSelected = 1;
										continue;
									}

									if (groupSelected) {
										isSelected = 0.5;
									}
								}

								if (isSelected === 0) {
									continue;
								}

								selectedMapping = mappingDef;
								selectedVariant = variantName;

								if (isSelected === 1) {
									break mapping;
								}
							}
						}

						if (selectedMapping && selectedVariant) {
							state.scenes[sceneId].variants[selectedVariant] = {
								selected: true,
							};
							material = await gltf.parser.getDependency(
								"material",
								selectedMapping.material,
							);
						}

						meshObject.material = material;
					}
				},
			);

			variantPromises.push(...promises);

			if (gltf.animations?.length) {
				for (const animationName in sceneConfig.animations) {
					const animation = gltf.animations.find(
						({ name }) => name === animationName,
					);

					if (!animation) {
						continue;
					}

					let isPlaying: boolean | null = null;

					for (const choice in selection) {
						if (selection[choice] === oldSelection[choice]) {
							continue;
						}
						const playing =
							sceneConfig.animations[animationName].play?.[choice]?.[
								selectionMap.get(choice) ?? ""
							];

						if (playing === false) {
							isPlaying = false;
							break;
						}

						if (playing === true) {
							isPlaying = true;
						}
					}

					if (isPlaying === null) {
						continue;
					}

					const animationJSON = gltf.parser.json.animations.find(
						({ name }: { name: string }) => name === animationName,
					);

					const nodes = animationJSON?.channels?.map(
						(channel: { target: { node: number } }) => {
							const nodeIndex = channel.target.node;
							return gltf.parser.json.nodes[nodeIndex].name;
						},
					);

					const isNodeVisible = nodes.some((nodeName: string) => {
						return (
							(state.scenes?.[sceneId]?.nodes?.[nodeName]?.visibility ??
								true) === true
						);
					});

					if (!isNodeVisible) {
						isPlaying = false;
					}

					state.scenes[sceneId].animations[animationName] = {
						play: isPlaying,
					};

					if (!isPlaying) {
						if (this.#animations[animationName]?.isRunning()) {
							this.#animations[animationName].clampWhenFinished = false;
							this.#animations[animationName].loop =
								ANIMATION_PLAYBACK_VALUES.once;
							this.#animations[animationName]?.fadeOut(0.3);
						} else {
							this.#animations[animationName]?.stop();
						}
						continue;
					}

					this.#animations[animationName] ??= this.#mixer.clipAction(animation);

					if (this.#animations[animationName].isRunning()) {
						continue;
					}

					const animationExt = animationJSON?.extensions?.DPL_animation_loop;

					const playbackValue = animationExt?.playback ?? "once";
					this.#animations[animationName].loop =
						ANIMATION_PLAYBACK_VALUES[playbackValue as "once" | "repeat"];
					this.#animations[animationName].clampWhenFinished = true;

					this.#animations[animationName].stop().play();
				}
			}
		}

		for (const hotspotName in this.#hotspots) {
			this.#hotspots[hotspotName]?.removeFromParent();
			delete this.#hotspots[hotspotName];
		}

		if (!disableHotspots) {
			for (const hotspotName in matrixJson.hotspots) {
				const hotspotConfig = matrixJson.hotspots[hotspotName];

				if (!hotspotConfig.element) {
					continue;
				}

				const visibility = Object.keys(hotspotConfig.visibility).every(
					(config) => {
						return (
							hotspotConfig.visibility[config]?.[
								selectionMap.get(config) as string
							] ?? true
						);
					},
				);

				this.#hotspots[hotspotName] = new CSSObject(hotspotConfig.element);
				this.#hotspots[hotspotName].position.set(
					...hotspotConfig.positions.default.value,
				);

				for (const position in hotspotConfig.positions) {
					const { value, selected } = hotspotConfig.positions[position];

					let isSelected: boolean | undefined;

					for (const choice in selected) {
						const value =
							selected[choice]?.[selectionMap.get(choice) as string];

						if (value === false) {
							isSelected = value;
							break;
						}
						isSelected ??= value;
					}

					if (isSelected) {
						this.#hotspots[hotspotName].position.set(...value);
						break;
					}
				}

				this.#hotspotGroup.add(this.#hotspots[hotspotName]);
				if (!visibility) {
					this.#hotspots[hotspotName]?.removeFromParent();
					delete this.#hotspots[hotspotName];
				}
			}
		}

		for (const [environmentId, environmentConfig] of Object.entries(
			matrixJson.environments,
		) || []) {
			const isSelected = Object.keys(selection).every(
				(choice) =>
					environmentConfig.selected[choice]?.[
						selectionMap.get(choice) as string
					] ?? true,
			);

			state.environments[environmentId] = {
				selected: isSelected,
			};

			if (isSelected) {
				const {
					intensity: _intensity,
					rotation: _rotation,
					...encodingOptions
				} = environmentConfig;

				if (this.scene.environment?.userData.environmentId === environmentId) {
					break;
				}

				this.scene.userData.selectedEnvironment = environmentId;
				const encodingOptionsJson = JSON.stringify(encodingOptions);

				if (environmentConfig.name) {
					const environmentResponse = await loadFile({
						owner: this.owner,
						workspace: this.workspace,
						projectName: this.projectName,
						productVersion: this.productVersion,
						filePath: environmentConfig.name as string,
						responseType: "buffer",
					});
					if (environmentResponse.isErr()) {
						return err(environmentResponse.error);
					}

					const fileObj = fileConvert(
						new Uint8Array(environmentResponse.value as ArrayBuffer),
					);

					const doppleHDRLoaderResult =
						await this.#doppleHDRLoader.loadWithMetaData(fileObj.url, {
							gamma: encodingOptions.gamma,
						});

					doppleHDRLoaderResult.mapping = EquirectangularReflectionMapping;
					doppleHDRLoaderResult.needsUpdate = true;

					doppleHDRLoaderResult.userData.environmentId = environmentId;

					this.scene.environment = doppleHDRLoaderResult;
				} else {
					this.scene.environment = this.#defaultEnvironment;
				}

				const { toneMapping, contrast, exposure, bloom } =
					environmentConfig.postprocessing ?? {};

				if (contrast) {
					this.contrastPass.uniforms.contrast.value = contrast;
				}

				if (exposure) {
					this.exposurePass.uniforms.exposure.value = exposure;
				}

				if (bloom?.enabled) {
					this.bloomPass.enabled = true;
					this.bloomPass.strength = bloom.strength;
					this.bloomPass.radius = bloom.radius;
					this.bloomPass.threshold = bloom.threshold;
				}

				this.renderer.toneMapping = (toneMapping ??
					environmentConfig.toneMapping) as ToneMapping;

				this.scene.environmentIntensity = environmentConfig.intensity;
				this.scene.environmentRotation.set(
					environmentConfig.rotation.x,
					environmentConfig.rotation.y,
					environmentConfig.rotation.z,
				);

				this.scene.environment.userData.encodingOptions = encodingOptionsJson;
				break;
			}
		}

		await Promise.all(variantPromises);

		let autoRotateEnabled: boolean | undefined;
		for (const choice of Object.keys(selection)) {
			const value =
				matrixJson.camera?.autoRotate?.[choice]?.[
					selectionMap.get(choice) as string
				];
			if (value === false) {
				autoRotateEnabled = value;
				break;
			}

			autoRotateEnabled ??= value;
		}
		this.#isAutoRotateEnabled = autoRotateEnabled;

		if (matrixJson.settings?.backgroundColor && this.matrix.settings) {
			this.matrix.settings.backgroundColor =
				matrixJson.settings.backgroundColor;
		}

		this.#applyAntiAliasingConfig(matrixJson);

		this.boundingBox = reduceVertices(
			this.#productGroup,
			(box, vertex) => box.expandByPoint(vertex),
			new Box3(),
		);

		this.boundingBox.getSize(this.size);

		this.boundingBox.getBoundingSphere(this.boundingSphere);
		const center = this.boundingSphere.center;

		this.boundingSphere.radius = Math.sqrt(
			reduceVertices(
				this.#productGroup,
				(value, vertex) => Math.max(value, center.distanceToSquared(vertex)),
				0,
			),
		);

		if (this.camera.userData.cameraKey !== selectedCamera.userData.cameraKey) {
			this.camera = selectedCamera;
			this.#resetControls();
			if (this.camera.name === "Viewport Camera") {
				fitAndRepositionCameraToProduct(this);
			}
		}

		updateCameraClippingPlanes(this, this.controls.camera);

		this.resetLabels();

		this.logger.info(
			{ event: "SELECTION_UPDATED", selection, oldSelection, state },
			"Selection Updated",
		);

		return ok(undefined);
	}

	#applyAntiAliasingConfig(matrixJson: Matrix) {
		const antiAliasing = matrixJson.postProcessing?.antiAliasing;
		if (!antiAliasing) {
			return;
		}

		const { msaa, taa } = antiAliasing;

		// Update TAA

		if (taa?.sampleLevel !== undefined) {
			const enableTAA = taa.sampleLevel !== -1;
			if (
				this.taaRenderPass.enabled !== enableTAA ||
				this.taaRenderPass.sampleLevel !== taa.sampleLevel
			) {
				this.taaRenderPass.enabled = enableTAA;
				if (enableTAA) {
					this.taaRenderPass.sampleLevel = taa.sampleLevel;
				}
			}
		}

		// Update MSAA
		if (
			msaa?.sampleLevel !== undefined &&
			this.renderTarget?.samples !== msaa.sampleLevel
		) {
			if (!this.renderTarget && msaa.sampleLevel === -1) {
				return;
			}
			this.#updateMSAA(msaa.sampleLevel);
		}
	}

	/**
	 * Renders the scene using both WebGL and CSS renderers. Updates control states
	 * and applies auto-rotation if enabled. The rendering process involves clearing
	 * the canvas, rendering the 3D scene with the main renderer, and then rendering
	 * CSS-based elements with the CSS renderer. Also updates animation mixer with
	 * the delta time.
	 */
	render() {
		const delta = this.#clock.getDelta();

		this.composer.reset();
		this.controls?.update(delta);

		if (
			(this.#isAutoRotateEnabled || this.camera.userData.autoRotateEnabled) &&
			!this.#pauseAutoRotate &&
			this.camera.userData.autoRotateSpeed
		) {
			this.controls.azimuthAngle = MathUtils.euclideanModulo(
				this.controls.azimuthAngle +
					this.camera.userData.autoRotateSpeed * delta * MathUtils.DEG2RAD,
				TWO_PI,
			);
		}

		this.#mixer.update(delta);

		this.renderer.autoClear = false;
		this.renderer.clear();
		this.composer.render();
		this.renderer.autoClear = true;

		this.cssRenderer.render(this.scene, this.camera);
	}

	/**
	 * Render the scene and then schedule the next frame.
	 */
	run = this.animate.bind(this);

	/**
	 * Render the scene and then schedule the next frame.
	 *
	 * This function is a no-op if the scene is paused.
	 */
	animate() {
		requestAnimationFrame(DoppleXR.prototype.animate.bind(this));

		this.render();
	}

	#observer = new ResizeObserver(() => {
		this.resize({ trackParentSize: true });
	});

	#logResizeEvent = debounce(
		({ trackParentSize = false }) =>
			this.logger.info(
				{ event: "CANVAS_RESIZE", trackParentSize },
				"Canvas Re-sized",
			),
		500,
	);

	/**
	 * Resize the canvas to the size of the container and update the camera's
	 * aspect ratio. If `trackParentSize` is `true`, the canvas will be resized
	 * automatically whenever the parent container is resized.
	 *
	 * @param resizeOptions
	 * @param resizeOptions.trackParentSize  Whether to auto resize the dopple element when container element gets resized
	 */
	resize({ trackParentSize = false } = {}) {
		this.renderer.setPixelRatio(window.devicePixelRatio);
		this.renderer.setSize(
			this.container.clientWidth,
			this.container.clientHeight,
		);

		this.composer.setSize(
			this.container.clientWidth,
			this.container.clientHeight,
		);

		this.cssRenderer.setSize(
			this.container.clientWidth,
			this.container.clientHeight,
		);

		this.bloomPass.setSize(
			this.container.clientWidth,
			this.container.clientHeight,
		);

		if (this.camera instanceof PerspectiveCamera) {
			this.camera.aspect =
				this.container.clientWidth / this.container.clientHeight;
		}
		this.camera.updateProjectionMatrix();

		if (trackParentSize) {
			this.#observer.observe(this.container);
		} else {
			this.#observer.disconnect();
		}

		this.#logResizeEvent({ trackParentSize });
	}

	/** Dispose of the scene and its associated resources. */
	[Symbol.dispose]() {
		for (const hotspotName in this.#hotspots) {
			this.#hotspots[hotspotName].removeFromParent();
			delete this.#hotspots[hotspotName];
		}

		this.logger.info({ event: "SESSION_ENDED" }, "Session ended");
		this.#logWriters.clear();

		this.renderer.renderLists.dispose();
		this.aria?.ariaContainer.remove();
		this.renderer.domElement.remove();
		this.renderer.dispose();
		this.animate = () => {};
		this.resetLabels = () => {};
		this.#observer.disconnect();
		this.#gragCursor?.destroy();
		this.#visibilityObserver.disconnect();
	}

	#resetLabels() {
		const children = Object.values(this.models).flatMap(
			({ scene }) => scene.children,
		);

		for (const hotspotName in this.#hotspots) {
			const cssObject = this.#hotspots[hotspotName];

			cssObject.getWorldPosition(this.raycaster.ray.origin);

			const rd = this.camera.position
				.clone()
				.sub(this.raycaster.ray.origin)
				.normalize();

			this.raycaster.ray.direction.set(rd.x, rd.y, rd.z);

			const hits = this.raycaster.intersectObjects(children);

			cssObject.visible = hits.length === 0;
		}
	}

	/**
	 * Adds the hotspot group to the scene, making them visible and interactable.
	 */
	enableHotspots() {
		this.scene.add(this.#hotspotGroup);
	}

	/**
	 * Removes the hotspot group from the scene, making them invisible and non-interactable.
	 */
	disableHotspots() {
		this.#hotspotGroup.removeFromParent();
	}

	/**
	 * @internal
	 */
	goalTarget: Vector3 = new Vector3();

	/**
	 * Sets the target position for the goalTarget vector.
	 *
	 * This method updates the goalTarget vector to the negated
	 * values of the provided model coordinates.
	 *
	 * @param modelX - The X coordinate of the model.
	 * @param modelY - The Y coordinate of the model.
	 * @param modelZ - The Z coordinate of the model.
	 */

	setTarget(modelX: number, modelY: number, modelZ: number) {
		this.goalTarget.set(-modelX, -modelY, -modelZ);
	}

	/**
	 * Retrieves the target position for the object.
	 *
	 * This method returns a clone of the goalTarget vector,
	 * multiplied by -1, effectively inverting its direction.
	 *
	 * @returns A Vector3 representing the target position.
	 */

	getTarget(): Vector3 {
		return this.goalTarget.clone().multiplyScalar(-1);
	}

	#targetDamperX = new Damper(50);
	#targetDamperY = new Damper(50);
	#targetDamperZ = new Damper(50);

	/**
	 * Smoothly updates the product group's position to the target position
	 * over time.
	 *
	 * @param delta - The time in milliseconds since the last frame.
	 * @returns A boolean indicating whether the product group was moved.
	 */
	updateTarget(delta: number): boolean {
		const goal = this.goalTarget;
		const target = this.#productGroup.position;
		if (!goal.equals(target)) {
			const normalization = this.boundingSphere.radius / 10;
			let { x, y, z } = target;
			x = this.#targetDamperX.update(x, goal.x, delta, normalization);
			y = this.#targetDamperY.update(y, goal.y, delta, normalization);
			z = this.#targetDamperZ.update(z, goal.z, delta, normalization);
			this.#productGroup.position.set(x, y, z);
			this.#productGroup.updateMatrixWorld();
			// this.queueRender();
			return true;
		}
		return false;
	}

	#eventListeners: { [K in keyof EventMap]: Set<(e: EventMap[K]) => void> } = {
		click: new Set(),
	};

	/** Appends event listener */
	addEventListener<K extends keyof EventMap>(
		/** Event type  */ event: K,
		/** Event listener callback to be added */ cb: (e: EventMap[K]) => void,
	): void {
		this.#eventListeners[event].add(cb);
	}

	/** Removes event listener  */
	removeEventListener<K extends keyof EventMap>(
		/** Event Type  **/ event: K,
		/** Event listener callback to be removed */ cb: (e: EventMap[K]) => void,
	): void {
		this.#eventListeners[event].delete(cb);
	}

	#getDefaultCamera() {
		const width = this.container.clientWidth ?? 1;
		const height = this.container.clientHeight ?? 1;
		const aspect = width / height;

		const camera = new PerspectiveCamera(50, aspect, 0.01, 250);
		camera.name = "Viewport Camera";
		camera.userData.cameraKey = camera.name;
		camera.position.set(0, 1.6, 3);
		camera.updateProjectionMatrix();
		return camera;
	}
}
